import axios from 'axios';
import { http } from './http.js';

export interface PublicCreatorInvitation {
  id: string;
  email: string;
  creatorName: string;
  organizationName: string;
  expiresAt: string;
  status: string;
}

export type CreatorInvitationStatus = 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED';

export interface CreatorInvitationListItem {
  id: string;
  creatorProfileId: string;
  email: string;
  creatorName: string;
  status: CreatorInvitationStatus;
  expiresAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export interface CreateCreatorInvitationResponse {
  invitation: CreatorInvitationListItem;
  /** Raw token — returned exactly once. Client must copy the URL now. */
  token: string;
  /** Full URL the agency can copy/share with the creator. */
  url: string;
  /** True when the server also emailed the link to the creator. */
  emailSent?: boolean;
}

export interface AcceptInvitationResult {
  token: string;
  user: {
    id: string;
    orgId: string;
    email: string;
    name: string;
    role: string;
    v1Role?: string | null;
    createdAt: string;
    lastLoginAt: string | null;
  };
  org: { id: string; name: string; slug: string; createdAt: string };
  app: string;
  invitationId: string;
}

export async function getPublicInvitation(rawToken: string): Promise<PublicCreatorInvitation> {
  const { data } = await http.get<{ success: boolean; invitation: PublicCreatorInvitation }>(
    `/public/creator-invitations/${encodeURIComponent(rawToken)}`,
  );
  return data.invitation;
}

export async function acceptInvitation(
  rawToken: string,
  payload: { password: string; name?: string },
): Promise<AcceptInvitationResult> {
  try {
    const { data } = await http.post<{ success: boolean } & AcceptInvitationResult>(
      `/public/creator-invitations/${encodeURIComponent(rawToken)}/accept`,
      payload,
    );
    return data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      const body = err.response?.data as { error?: { message?: string }; message?: string } | undefined;
      const message = body?.error?.message ?? body?.message ?? err.message;
      throw new Error(message);
    }
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Agency-facing invitation management (Mode B "Add Creator" in the uploader).
// These call the v1 surface; the backend enforces creator.manage RBAC for the
// CONTENT_UPLOADER application and scopes every row to the caller's org.
// ---------------------------------------------------------------------------

export async function createCreatorInvitation(params: {
  email: string;
  creatorName: string;
}): Promise<CreateCreatorInvitationResponse> {
  const { data } = await http.post<{ success: boolean } & CreateCreatorInvitationResponse>(
    '/v1/creator-invitations',
    params,
  );
  return data;
}

export async function listCreatorInvitations(filters?: {
  status?: string;
}): Promise<{ success: boolean; invitations: CreatorInvitationListItem[] }> {
  const { data } = await http.get<{ success: boolean; invitations: CreatorInvitationListItem[] }>(
    '/v1/creator-invitations',
    { params: filters },
  );
  return data;
}

export async function revokeCreatorInvitation(
  id: string,
): Promise<{ success: boolean; invitation: CreatorInvitationListItem }> {
  const { data } = await http.post<{ success: boolean; invitation: CreatorInvitationListItem }>(
    `/v1/creator-invitations/${encodeURIComponent(id)}/revoke`,
  );
  return data;
}

export async function regenerateCreatorInvitation(
  id: string,
): Promise<CreateCreatorInvitationResponse> {
  const { data } = await http.post<{ success: boolean } & CreateCreatorInvitationResponse>(
    `/v1/creator-invitations/${encodeURIComponent(id)}/regenerate`,
  );
  return data;
}